import { useEffect, useRef, useState } from "react";
import { Flame, Sparkles, Activity, Users2, Film, Zap } from "lucide-react";
import { ConfidenceBadge } from "@/components/viral/ConfidenceBadge";
import {
  usePublicEmotionalSignal,
  type PublicEmotionalCtx,
} from "@/hooks/usePublicEmotionalSignal";
import type { DominantKind } from "@/lib/viralIntel/emotionalIntensityCoordinator";
import { pickCreatorHook, pickFlexLine } from "@/lib/viralIntel/socialFlexCopy";
import { CompetitiveWatchModeBadge } from "@/components/public/CompetitiveWatchModeBadge";
import { useEdgeSafeFlags } from "@/lib/resilience/edgeSafeFlags";
import {
  applyStadiumState,
  deriveStadiumState,
  resetStadiumState,
} from "@/lib/pr36/stadiumVibeStateEngine";

/**
 * PublicEmotionalSignalOverlay
 * ----------------------------
 * Public, ultra-minimal cinematic overlay that surfaces ONE emotional signal
 * at a time (heat / story arc / contrarian / viral / crowd) chosen by the
 * EmotionalIntensityCoordinator. Screenshot-friendly. No spam.
 *
 * - lightweight: no extra polling, reuses the derived snapshot
 * - temporal: auto-hides shortly after the signal weakens
 * - cinematic: glow + confidence badge + dominant-kind glyph
 */
const ICON_BY_KIND: Record<Exclude<DominantKind, "idle">, typeof Flame> = {
  heat_window: Flame,
  story_arc: Film,
  contrarian: Sparkles,
  viral_card: Zap,
  crowd: Users2,
};

const ACCENT_BY_KIND: Record<Exclude<DominantKind, "idle">, string> = {
  heat_window: "oklch(0.7 0.25 27)",
  story_arc: "oklch(0.7 0.25 27)",
  contrarian: "oklch(0.82 0.2 60)",
  viral_card: "oklch(0.7 0.25 27)",
  crowd: "oklch(0.82 0.24 145)",
};

const LABEL_BY_KIND: Record<Exclude<DominantKind, "idle">, string> = {
  heat_window: "HEAT WINDOW",
  story_arc: "STORY ARC",
  contrarian: "CONTRARIAN READ",
  viral_card: "VIRAL MOMENT",
  crowd: "CROWD SIGNAL",
};

export function PublicEmotionalSignalOverlay(props: PublicEmotionalCtx & {
  /** Optional className for the wrapper. */
  className?: string;
}) {
  const sig = usePublicEmotionalSignal(props);
  const flags = useEdgeSafeFlags();
  const [visibleKey, setVisibleKey] = useState<string | null>(null);
  // F3-01 fix — cooldown 12s between consecutive signals of the same kind
  // to prevent overlay fatigue when signals rotate rapidly.
  const lastShownAtRef = useRef<Record<string, number>>({});

  // PR-36S — drive the global stadium vibe state from the existing
  // emotional signal. CSS ambient layer + identity tokens react via
  // `<html data-stadium-state>` without any extra polling.
  useEffect(() => {
    const vibe = deriveStadiumState({
      kind: sig.idle ? "idle" : sig.kind,
      intensity: sig.intensity,
      scoreHome: props.scoreHome,
      scoreAway: props.scoreAway,
      density: flags.allowCinematic ? flags.density : 0,
      suppressNoise: sig.suppressNoise,
    });
    applyStadiumState(vibe);
  }, [
    sig.idle,
    sig.kind,
    sig.intensity,
    sig.suppressNoise,
    props.scoreHome,
    props.scoreAway,
    flags.allowCinematic,
    flags.density,
  ]);
  useEffect(() => () => resetStadiumState(), []);

  // Temporal pacing — only render when a non-idle signal lives for at least
  // one snapshot, and let it auto-fade ~7s after the headline changes.
  useEffect(() => {
    // Visibility / kill-switch gating: when the tab is hidden or overlays
    // are disabled, freeze instantly instead of timing out.
    if (sig.idle || flags.screenshotFreezeMode || !flags.allowCinematic) {
      setVisibleKey(null);
      return;
    }
    const key = `${sig.kind}|${sig.headline}`;
    // Suppress same-kind re-trigger within 12s of last appearance.
    const now = Date.now();
    const lastAt = lastShownAtRef.current[sig.kind] ?? 0;
    if (now - lastAt < 12_000) {
      return;
    }
    lastShownAtRef.current[sig.kind] = now;
    setVisibleKey(key);
    const id = window.setTimeout(() => {
      setVisibleKey((cur) => (cur === key ? null : cur));
    }, 7000);
    return () => window.clearTimeout(id);
  }, [sig.idle, sig.kind, sig.headline, flags.screenshotFreezeMode, flags.allowCinematic]);

  if (sig.idle || !visibleKey || sig.kind === "idle") return null;
  if (flags.screenshotFreezeMode || !flags.allowCinematic) return null;

  const Icon = ICON_BY_KIND[sig.kind];
  const accent = ACCENT_BY_KIND[sig.kind];
  const label = LABEL_BY_KIND[sig.kind];
  const seed =
    Math.round(sig.intensity * 1000) +
    (props.scoreHome ?? 0) * 7 +
    (props.scoreAway ?? 0) * 3;
  const lang = props.lang ?? "ES";
  const flexLine = pickFlexLine(sig.kind, seed, lang);
  const creatorHook = pickCreatorHook(seed, lang);
  const highVirality = sig.intensity >= 0.65;
  const showCreatorHook = highVirality && flags.allowCreatorHooks;
  const accentColor = accent;

  return (
    <div
      className={`pointer-events-none animate-fade-in ${props.className ?? ""}`}
      data-screenshot-target="public-emotional-signal"
    >
      <div
        className="relative overflow-hidden rounded-xl border border-border bg-[oklch(0.06_0.005_260)] px-3 py-2 text-[oklch(1_0_0)] shadow-lg"
        style={{ boxShadow: `inset 0 0 0 1px ${accentColor}` }}
      >
        <div className="mb-1.5 flex items-center gap-1.5">
          <CompetitiveWatchModeBadge dominantKind={sig.kind} />
          {showCreatorHook && (
            <span className="rounded-full bg-[oklch(1_0_0)] px-2 py-0.5 text-[9px] font-black uppercase tracking-normal text-[oklch(0.06_0.005_260)]">
              {creatorHook}
            </span>
          )}
        </div>
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 rounded-md bg-[oklch(1_0_0)] p-1.5 ring-1 ring-border">
            <Icon className="h-3.5 w-3.5 text-[oklch(0.06_0.005_260)]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-normal text-[oklch(1_0_0)]">
              <Activity className="h-2.5 w-2.5" />
              <span>{label}</span>
            </div>
            <h4 className="mt-0.5 text-[14px] font-black leading-tight text-[oklch(1_0_0)]">
              {sig.headline}
            </h4>
            <p className="mt-0.5 line-clamp-2 text-[13px] font-semibold leading-snug text-[oklch(1_0_0)]">
              {sig.detail}
            </p>
            <p className="mt-1 text-[12px] font-bold italic text-[oklch(1_0_0)]">
              {flexLine}
            </p>
          </div>
          <ConfidenceBadge rating={sig.rating} tier="minimal" className="shrink-0" />
        </div>
        <div
          className="absolute inset-x-0 bottom-0 h-[2px]"
          style={{ width: `${Math.round(sig.intensity * 100)}%`, backgroundColor: accentColor }}
        />
      </div>
    </div>
  );
}
